// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import OnLinkDialog from 'Ui/components/common/onlink-dialog/onlink-dialog';
import {getEquipmentDetailsTabs} from 'OnEquip/equipment/equipment-details/utils/equipment-details-helper';
import {getEquipment} from 'Services/equipment-service';
import {fetchEffectData} from 'Utils/react-utils';
import {redirect as redirectTo} from 'Utils/redirect-utils';
import {useEquipmentContext} from 'OnEquip/equipment/context/equipment-context';
import {EQUIPMENT_DETAILS} from 'Common/constants/routes';
import {getHierarchy} from 'OnEquip/equipment/utils/membership-hierarchy-utils';
import {VIEW_EQUIPMENT_DETAILS} from 'Common/constants/business-activities';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {
    openDialog as openReduxDialog,
    closeDialog as closeReduxDialog
} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {MAINTENANCE_MANAGER_PROGOLF} from 'Common/constants/isg-toggles';

function initializeState() {
    const [equipment, setEquipment] = React.useState({});
    const [membershipHierarchy, setMembershipHierarchy] = React.useState({});
    const [loading, setLoading] = React.useState(true);

    return {
        equipment,
        setEquipment,
        loading,
        setLoading,
        membershipHierarchy,
        setMembershipHierarchy
    };
}

function EquipmentDetails(props) {
    const {
        appUserMembershipId,
        equipmentId,
        history,
        redirect,
        superUser,
        tab,
        translations,
        isgToggles,
        isMigrated,
        myJdPermissions,
        openAddEditExpenseDialog,
        openConfirmation,
        closeConfirmation,
        currencyPreference
    } = props;

    const isFleetToMaintenanceEnabled = isgToggles?.[MAINTENANCE_MANAGER_PROGOLF];

    const {
        equipment,
        setEquipment,
        loading,
        setLoading,
        membershipHierarchy,
        setMembershipHierarchy
    } = initializeState();

    const canViewEquipmentDetails = isAuthorized(
        {
            myJdPermissions: VIEW_EQUIPMENT_DETAILS
        }, {
            isMigrated,
            myJdPermissions
        }
    );

    async function getEquipmentCall(isMounted = () => true) {
        setLoading(true);

        const [equipment, hierarchy] = await Promise.all([
            getEquipment(equipmentId),
            getHierarchy(superUser, appUserMembershipId)
        ]);

        if (isMounted()) {
            setMembershipHierarchy(hierarchy);
            setEquipment(equipment);
            setLoading(false);
        }
    }

    const equipmentContext = useEquipmentContext();

    function updateEquipment() {
        getEquipmentCall();
        equipmentContext.current();
    }

    React.useEffect(() => canViewEquipmentDetails ? fetchEffectData(getEquipmentCall) : undefined,
        [equipmentId, appUserMembershipId, canViewEquipmentDetails]);

    const tabs = getEquipmentDetailsTabs(translations, equipment, membershipHierarchy, loading, updateEquipment,
        isMigrated, myJdPermissions, openAddEditExpenseDialog, openConfirmation, closeConfirmation, currencyPreference, isFleetToMaintenanceEnabled);

    const tabsWithPath = tabs.map((item) => ({
        ...item,
        title: item.label,
        path: `${redirect.path}${EQUIPMENT_DETAILS}/${item.id}`
    }));

    return (
        <OnLinkDialog
            activeTab={tab}
            className='equipment-details settings-dialog'
            closeHandler={() => redirectTo(redirect.path, history)}
            content={tabsWithPath}
            large={true}
            onTabClick={(tabId) => redirectTo(`${redirect.path}${EQUIPMENT_DETAILS}/${tabId}?equipmentId=${equipmentId}`, history)}
            routable={true}
            showError={!canViewEquipmentDetails}
            title={translations.equipment_details}
        />
    );
}

EquipmentDetails.propTypes = {
    appUserMembershipId: PropTypes.string,
    closeConfirmation: PropTypes.func,
    currencyPreference: PropTypes.string,
    equipmentId: PropTypes.string,
    history: PropTypes.history,
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    myJdPermissions: PropTypes.myJdPermissions,
    openAddEditExpenseDialog: PropTypes.func,
    openConfirmation: PropTypes.func,
    redirect: PropTypes.object,
    superUser: PropTypes.bool,
    tab: PropTypes.string,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        appUserMembershipId: state.account.membershipId,
        superUser: state.account.superUser,
        isgToggles: state.account.isgToggles,
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions,
        currencyPreference: state.membership.currencyPreference
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        openAddEditExpenseDialog(props) {
            dispatch(openReduxDialog(dialogTypes.ADD_EDIT_EXPENSE_DIALOG, props));
        },
        openConfirmation(props) {
            dispatch(openReduxDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        closeConfirmation() {
            dispatch(closeReduxDialog(dialogTypes.CONFIRMATION_DIALOG));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EquipmentDetails));
